import { Button } from "@/components/button";
import { DateInput } from "@/components/date-input";
import { TimePill } from "@/components/pill";
import { Section } from "@/components/section";
import { Select } from "@/components/select";
import { TimeInput } from "@/components/time-input";
import { WEEKDAYS } from "@/utils/constants";
import { css } from "@/utils/css";
import { formatTime } from "@/utils/format";
import { __ } from "@wordpress/i18n";
import { formatDistance } from "date-fns";
import { useCallback, useState } from "react";

type ScheduleState = {
    weekdays: number[];
    times: { id: string; hour: number; minute: number }[];
};

type Props = React.HTMLProps<HTMLDivElement> & {
    expDate: Date;
    setExpDate: React.Dispatch<React.SetStateAction<Date>>;
    schedule: ScheduleState;
    setSchedule: React.Dispatch<React.SetStateAction<ScheduleState>>;
};

const weekdays = WEEKDAYS.map((day) => ({ value: day.value.toString(), label: day.label }));

export function Schedule({ expDate, setExpDate, schedule, setSchedule }: Props) {
    const now = new Date();

    const [time, setTime] = useState({ hour: now.getHours(), minute: now.getMinutes() });
    const isEveryday = schedule.weekdays.length === 7;

    const handleAddTime = useCallback(
        (hour: number, minute: number) => {
            const time = { id: formatTime(hour, minute), hour, minute };

            if (schedule.times.find((t) => t.id === time.id)) {
                return;
            }

            setSchedule((prev) => ({ ...prev, times: [...prev.times, time] }));
        },
        [schedule]
    );

    return (
        <Section className={css("bg-light flex flex-col gap-6 mt-8")}>
            <h3>{__("Schedule Posting Frequency", "antimanual")}</h3>
            <div className={css("grid grid-cols-2 gap-6")}>
                <Select
                    label={__("Repeat On", "antimanual")}
                    help={__("Select how often you want to generate posts.", "antimanual")}
                    value={isEveryday ? "everyday" : "specific-days"}
                    onChange={(value) => setSchedule((prev) => ({ ...prev, weekdays: value === "everyday" ? [0, 1, 2, 3, 4, 5, 6] : [] }))}
                    options={[
                        { value: "everyday", label: __("Everyday", "antimanual") },
                        { value: "specific-days", label: __("Specific Days", "antimanual") },
                    ]}
                />
                {!isEveryday && (
                    <Select
                        label={__("Select Days", "antimanual")}
                        isMulti
                        options={weekdays}
                        value={schedule.weekdays.map(String)}
                        onChange={(days) => setSchedule((prev) => ({ ...prev, weekdays: days.map(Number) }))}
                    />
                )}
            </div>
            <div className={css("flex items-end gap-2")}>
                <TimeInput label={__("Time", "antimanual")} value={time} onChange={setTime} />
                <Button small className={css("add-time")} onClick={() => handleAddTime(time.hour, time.minute)}>
                    {__("Add Time", "antimanual")}
                </Button>
            </div>
            {schedule.times.length > 0 && (
                <div className={css("flex flex-wrap gap-1")}>
                    {schedule.times.map((t) => (
                        <div key={t.id} className={css("time-pill")}>
                            <TimePill hour={t.hour} minute={t.minute} />
                            <span
                                className={css("remove")}
                                onClick={() => setSchedule((prev) => ({ ...prev, times: prev.times.filter((time) => time.id !== t.id) }))}
                            >
                                ×
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <div className={css("grid grid-cols-2")}>
                <DateInput
                    includeTime
                    label={__("Expiration Date", "antimanual")}
                    help={__("Auto Posting will be stopped after", "antimanual") + " " + formatDistance(expDate, now) + "."}
                    value={expDate}
                    onChange={(date) => {
                        if (date > now) {
                            setExpDate(date);
                        }
                    }}
                />
            </div>
        </Section>
    );
}
